import { useCallback, useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const NAV = [
  { label: "Listings", href: "/#services" },
  { label: "Packages", href: "/#packages" },
  { label: "Agency", href: "/#agency" },
  { label: "Contact", href: "/#contact" },
];

const DesktopNav = ({ navFg }) => (
  <nav className="hidden md:flex items-center gap-10">
    {NAV.map((n) => (
      <a
        key={n.href}
        href={n.href}
        data-testid={`nav-${n.label.toLowerCase()}`}
        className={`text-[13px] tracking-wide link-underline transition-colors ${navFg}`}
      >
        {n.label}
      </a>
    ))}
  </nav>
);

const MobileMenu = ({ onClose }) => (
  <div data-testid="mobile-menu" className="md:hidden bg-white border-t border-black/10">
    <div className="px-6 py-6 flex flex-col gap-5">
      {NAV.map((n) => (
        <a
          key={n.href}
          href={n.href}
          onClick={onClose}
          data-testid={`mobile-nav-${n.label.toLowerCase()}`}
          className="font-serif-display text-2xl text-black"
        >
          {n.label}
        </a>
      ))}
      <a
        href="https://portal.structuremedia.me/book"
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClose}
        data-testid="mobile-book-cta"
        className="mt-2 inline-flex items-center justify-between bg-black text-white px-5 py-4 text-[12px] tracking-[0.18em] uppercase"
      >
        Book a shoot <span>→</span>
      </a>
    </div>
  </div>
);

const BrandLogo = ({ onHero, fg, subFg }) => (
  <a
    href="/"
    data-testid="brand-logo"
    className={`flex items-center gap-3 leading-none group ${fg}`}
  >
    <img
      src="/brand/logo-full-dark.png"
      alt="structure media"
      className={`h-8 md:h-9 w-auto block ${onHero ? "hidden" : ""}`}
    />
    <img
      src="/brand/logo-full-white.png"
      alt="structure media"
      className={`h-8 md:h-9 w-auto block ${onHero ? "" : "hidden"}`}
    />
    <span className={`hidden lg:block subhead text-[9px] ${subFg}`}>
      Creators. Inside &amp; Out.
    </span>
  </a>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const onScroll = useCallback(() => setScrolled(window.scrollY > 24), []);
  const closeMenu = useCallback(() => setOpen(false), []);

  useEffect(() => {
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  // When sitting over the video hero, render in white. Otherwise, black-on-white.
  const onHero = !scrolled && !open;
  const fg = onHero ? "text-white" : "text-black";
  const subFg = onHero ? "text-white/70" : "text-black/55";
  const navFg = onHero
    ? "text-white/85 hover:text-white"
    : "text-black/80 hover:text-black";
  const ctaCls = onHero
    ? "bg-white text-black hover:bg-[#c9a878]"
    : "bg-black text-white hover:bg-[#c9a878] hover:text-black";

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-md bg-[rgba(255,255,255,0.82)] border-b border-black/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <BrandLogo onHero={onHero} fg={fg} subFg={subFg} />

        <DesktopNav navFg={navFg} />

        <div className="hidden md:block">
          <a
            href="https://portal.structuremedia.me/book"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-book-cta"
            className={`inline-flex items-center gap-2 px-5 py-3 text-[12px] tracking-[0.18em] uppercase font-medium transition-colors duration-300 ${ctaCls}`}
          >
            Book a shoot
            <span aria-hidden>→</span>
          </a>
        </div>

        <button
          className={`md:hidden p-2 ${fg}`}
          aria-label="Open menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && <MobileMenu onClose={closeMenu} />}
    </header>
  );
}
